{
  "pages": [
    {
      "guideType": "learn",
      "slug": "agentic-workflows",
      "title": "Meet Gab AI Work",
      "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact.",
      "navIcon": "briefcase",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 700,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I selected Work and sent a small, specific task.",
          "I opened the delivered artifact.",
          "I requested a focused revision in the same task."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "From an idea to a deliverable",
            "copy": "Work is Gab AI’s workspace for tasks that take more than one answer. It can research, write and run code, create files, and use media tools as it works toward your brief. You stay in the same Gab AI conversation to answer questions, review progress, and ask for changes. There is no separate coding agent to install for these lessons."
          },
          {
            "type": "section",
            "title": "1. Start a Work task",
            "copy": "Sign in to Gab AI and select Work in the composer. Describe the outcome, attach any references, and name the artifact you want. Review the available Work options and your credits, then press Send. These lessons are free to read; running Work and generating media uses credits."
          },
          {
            "type": "work-prompt",
            "title": "Your first small task",
            "code": "Create an editable one-page project brief for a fictional browser game called Star Collector. Include the goal, audience, controls, three must-have features, and a checklist for testing. Keep the scope small and deliver the document as an artifact."
          },
          {
            "type": "section",
            "title": "2. Follow progress and answer questions",
            "copy": "Read Work’s progress updates to see what it is doing. When it asks for missing details, answer in the task. A plan or progress message is not the finished output. Wait for the delivered file, or read the explanation if Work cannot finish."
          },
          {
            "type": "section",
            "title": "3. Open the artifact",
            "copy": "An artifact is a result you can open: a document, spreadsheet, deck, image, code file, or video project. Open the file card in the conversation to preview or edit it. The editor and available actions depend on the file type and device; these walkthroughs use Gab AI on the web."
          },
          {
            "type": "section",
            "title": "4. Review, then revise",
            "copy": "Compare the result with your brief. Make a direct edit where supported or request a focused change in the same task. Name the artifact and what must stay the same. Download or export the final version and inspect the file you will actually use."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "I selected Work and sent a small, specific task.",
              "I opened the delivered artifact.",
              "I requested a focused revision in the same task."
            ]
          }
        ],
        "title": "Meet Gab AI Work | Gab AI Learn",
        "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, meet Gab AI Work"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/agentic-workflows",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "meet Gab AI Work"
      ],
      "navigation": {
        "next": {
          "label": "Your artifact toolkit",
          "href": "/learn/agent-tools"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "agent-tools",
      "title": "Your artifact toolkit",
      "description": "Know which artifact to request, what you can edit, and how to take your work with you.",
      "navIcon": "layer-group",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 701,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I can name the artifact and export format for my project.",
          "I know when Add to chat still requires Send.",
          "I understand that a revised project needs a fresh export."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Choose the result before choosing the tool",
            "copy": "Tell Work what you want to make. It selects tools while you describe the result and review it. Ask for the editable artifact as well as any final download you need. An answer pasted into chat, an editable project, and an exported file serve different purposes."
          },
          {
            "type": "unordered-list",
            "title": "What each artifact is for",
            "items": [
              "Document: edit text, headings and tables, use AI rewrite, then export Word or PDF.",
              "Spreadsheet: edit cells, formulas, formatting and sheets, then export Excel.",
              "Presentation: edit slides and speaker notes, rehearse with Present, then export PowerPoint or PDF.",
              "Image: inspect versions, select an area, request edits or reframing, then download the chosen image.",
              "Code and HTML: run supported HTML in Preview, inspect Code (or Source for Markdown) and available Changes, and point to revisions with Select or Highlight. Source is a viewer; ask Work for code changes.",
              "Video project: review reference media and clips, compare takes, and create a final video export."
            ]
          },
          {
            "type": "section",
            "title": "1. Open the delivered file",
            "copy": "Choose the artifact card in the Work conversation. A supported HTML file opens a preview, while other code types show source. A PDF is a finished document preview; ask for an editable document when you need to change its content inside Gab AI."
          },
          {
            "type": "section",
            "title": "2. Use the right kind of edit",
            "copy": "Text, cells and slides support direct editing in their respective editors. In the image studio and HTML preview, Add to chat prepares an AI revision: you still review and send it in chat. Saving a direct edit and asking Work to generate a new result are different actions."
          },
          {
            "type": "section",
            "title": "3. Keep the deliverable current",
            "copy": "Wait for saves to finish, compare the revised artifact with the original, and export again after changes. For a multi-file project, ask Work for all required assets and instructions. Check the downloaded file before sending it to someone else."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "I can name the artifact and export format for my project.",
              "I know when Add to chat still requires Send.",
              "I understand that a revised project needs a fresh export."
            ]
          }
        ],
        "title": "Your artifact toolkit | Gab AI Learn",
        "description": "Know which artifact to request, what you can edit, and how to take your work with you. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, your artifact toolkit"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/agent-tools",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "your artifact toolkit"
      ],
      "navigation": {
        "previous": {
          "label": "Meet Gab AI Work",
          "href": "/learn/agentic-workflows"
        },
        "next": {
          "label": "Write a useful Work brief",
          "href": "/learn/briefing-agents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "briefing-agents",
      "title": "Write a useful Work brief",
      "description": "Specify the outcome, references, constraints, review points, and definition of done.",
      "navIcon": "list-check",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 702,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My brief names an output format and audience.",
          "My sources and scope are explicit.",
          "I included a review point and a concrete acceptance check."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Make success observable",
            "copy": "A good Work brief describes the result someone will use. Include the audience, exact output formats, source material, limits, and a test that proves the work is ready. State assumptions Work may make and which choices need your input."
          },
          {
            "type": "work-prompt",
            "title": "A reusable Work brief",
            "code": "Outcome: Create [deliverable] for [audience].\nContext: Use [attached files or facts]. Preserve [details].\nFormat: Deliver [editable artifact] plus [export format].\nScope: Include [must-haves]. Keep this first version to [limit].\nReview: Pause after [storyboard, outline, or prototype] so I can review it.\nDone means: Check [specific tests] and explain anything incomplete."
          },
          {
            "type": "section",
            "title": "1. Attach and explain references",
            "copy": "Upload the relevant source and identify its role: “Use this as factual source material” or “Match this visual style.” State which file is authoritative if several disagree. Work only has the context available in the task; do not assume it can see a file on your device that you have not attached."
          },
          {
            "type": "section",
            "title": "2. Choose an appropriate review point",
            "copy": "For a short document, ask for a complete first draft. For a video, approve the storyboard and references before generating every clip. A requested pause is an instruction to Work, so check that it has reached the review point before approving the next phase."
          },
          {
            "type": "section",
            "title": "3. Define checks and delivery",
            "copy": "For a game, list controls, collisions and restart. For a budget, give a known total. For a deck, ask it to inspect every slide. Request the actual files and an explanation of what was verified, rather than treating a confident completion message as proof."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "My brief names an output format and audience.",
              "My sources and scope are explicit.",
              "I included a review point and a concrete acceptance check."
            ]
          }
        ],
        "title": "Write a useful Work brief | Gab AI Learn",
        "description": "Specify the outcome, references, constraints, review points, and definition of done. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, write a useful work brief"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/briefing-agents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "write a useful work brief"
      ],
      "navigation": {
        "previous": {
          "label": "Your artifact toolkit",
          "href": "/learn/agent-tools"
        },
        "next": {
          "label": "Review, revise, and finish",
          "href": "/learn/running-agent-loops"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "running-agent-loops",
      "title": "Review, revise, and finish",
      "description": "Steer a Work task with focused feedback and verify the final artifact.",
      "navIcon": "arrows-rotate",
      "navSectionSlug": "module-7-agentic-workflows",
      "navOrder": 703,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My revision identifies an observed issue and expected behavior.",
          "I tested the updated artifact instead of relying on the reply.",
          "I opened the final download and checked that it is the intended version."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Use a short feedback loop",
            "copy": "Start with a useful first version. Open the result, find the most important mismatch, and request a specific revision. Keep related changes in the same Work task so the brief, source files, and prior results stay together."
          },
          {
            "type": "section",
            "title": "1. Review against the brief",
            "copy": "Open and use the artifact. Play the game, change a spreadsheet input, read the document, or watch the full video with sound. Write down observed behavior and expected behavior. A screenshot helps with layout; reproduction steps help with broken interactions."
          },
          {
            "type": "work-prompt",
            "title": "A revision Work can act on",
            "code": "Update the existing Star Collector game artifact. On a narrow phone screen, the touch buttons cover the score. Move the controls below the game area and keep the score visible. Preserve keyboard controls, scoring, and artwork. Test a 390-pixel-wide layout and restart, then deliver the updated HTML."
          },
          {
            "type": "section",
            "title": "2. Preserve what already works",
            "copy": "Name the file, slide, scene, cell range, or selected region. Say what to change and what to keep. Use the artifact’s selection controls when available. For image and HTML requests prepared with Add to chat, inspect the drafted message and press Send."
          },
          {
            "type": "section",
            "title": "3. Recover from an incomplete task",
            "copy": "Read any failure or credit message before retrying. If the task is still running, avoid repeatedly launching the same job. You can stop Work when needed; stopping does not turn a partial result into a completed file. Resume with a specific next step, such as fixing one failed clip or finishing one export."
          },
          {
            "type": "section",
            "title": "4. Verify and take the work with you",
            "copy": "Recheck the changed area and the key behavior around it. Wait for editor saves or video export completion. Download the latest file and open it outside the preview. Keep editable artifacts for future changes and use exported files for delivery."
          },
          {
            "type": "project-checklist",
            "title": "Try it yourself",
            "items": [
              "My revision identifies an observed issue and expected behavior.",
              "I tested the updated artifact instead of relying on the reply.",
              "I opened the final download and checked that it is the intended version."
            ]
          }
        ],
        "title": "Review, revise, and finish | Gab AI Learn",
        "description": "Steer a Work task with focused feedback and verify the final artifact. Follow the steps in Gab AI Work. Review and export your result.",
        "keywords": "Gab AI, Work, artifacts, review, revise, and finish"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/running-agent-loops",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "review, revise, and finish"
      ],
      "navigation": {
        "previous": {
          "label": "Write a useful Work brief",
          "href": "/learn/briefing-agents"
        },
        "next": {
          "label": "Build a playable video game",
          "href": "/learn/work-games"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-games",
      "title": "Build a playable video game",
      "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game.",
      "navIcon": "gamepad",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 704,
      "guideSeo": {
        "readMinutes": 18,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I can move, collect stars, win or lose, and restart without reloading.",
          "The controls fit on a small screen and do not cover the game.",
          "I reviewed a revision and tested the downloaded HTML."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A playable HTML game",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 18,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Start with one complete game loop",
            "copy": "Open Gab AI, select Work, and paste the starter prompt below. Keep the first version small: movement, a goal, obstacles, and restart. Work will create the files and check the game. Answer any question about the controls or scope in the same task."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Build Star Collector, a small playable browser game in one self-contained HTML file with embedded CSS and JavaScript. Move with arrow keys or WASD and on-screen touch controls. Collect 10 stars, avoid three moving obstacles, show score and a win or lose screen, and include a working Restart button. Use a midnight-blue space theme. No external assets, logins, or network calls. Test movement, collisions, scoring, restart, and a narrow mobile layout. Deliver the HTML game as a file artifact and tell me what you tested."
          },
          {
            "type": "section",
            "title": "2. Open the code artifact and play",
            "copy": "When Work delivers the HTML file, open its file card. Preview runs the game; click inside it before using keyboard controls. Collect stars, hit an obstacle, reach the win screen, and restart. Use the preview device selector to check a phone-sized layout. Test touch controls on a real touch device too."
          },
          {
            "type": "section",
            "title": "3. Point to a change",
            "copy": "In the HTML preview, use Select to pick a page element or Highlight to mark a region. Choose Ask for changes and describe the revision. A game drawn on a canvas is usually one element: highlight the region and describe the gameplay issue in words. Add to chat prepares the request; review it and press Send in the conversation."
          },
          {
            "type": "section",
            "title": "4. Inspect the revision",
            "copy": "Open the updated artifact. The Code tab lets you read the HTML file; Changes appears when an earlier source is available. For a simple static text element, Select may offer Edit text and Save copy. Use Work for game logic and dynamic text. Replay your test sequence after every change."
          },
          {
            "type": "section",
            "title": "5. Download and test the file",
            "copy": "Download the final HTML and open it in your browser. Because this exercise embeds its assets, it should run without a server. Check the downloaded copy as well as the preview. If you later request separate assets, ask Work to package the complete folder with instructions."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In Star Collector, make the player 20% slower and enlarge the touch controls. Keep the scoring, star count, visual style, and restart behavior. Update the existing game artifact and retest keyboard movement, collisions, winning, and restart."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If the preview is blank, try Restart preview, then tell Work what you see and ask it to fix the HTML artifact. If controls do nothing, click into Preview and leave Select or Highlight mode. If only the download fails, report that separately from the preview."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I can move, collect stars, win or lose, and restart without reloading.",
              "The controls fit on a small screen and do not cover the game.",
              "I reviewed a revision and tested the downloaded HTML."
            ]
          }
        ],
        "title": "Build a playable video game | Gab AI Learn",
        "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, build a playable video game"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-games",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a playable video game"
      ],
      "navigation": {
        "previous": {
          "label": "Review, revise, and finish",
          "href": "/learn/running-agent-loops"
        },
        "next": {
          "label": "Create and edit an image",
          "href": "/learn/work-images"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-images",
      "title": "Create and edit an image",
      "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format.",
      "navIcon": "image",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 705,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I inspected the image at a useful zoom.",
          "I sent one focused edit from Add to chat and compared the result.",
          "I downloaded the intended version and checked its dimensions."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An image and a revised version",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Give Work an image brief",
            "copy": "Select Work and send the prompt below. Name the subject, style, shape, and where the image will be used. You can attach a reference image and explain which details to preserve. For this exercise, keep the cover free of lettering so you can judge the composition first."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create a square cover image for a browser game called Star Collector: a friendly small astronaut reaching for glowing gold stars, midnight-blue space, simple bold shapes, playful illustrated style. Leave clear space at the top for a title I will add later. Do not generate lettering. Deliver the image as an artifact."
          },
          {
            "type": "section",
            "title": "2. Open the image studio",
            "copy": "Open the delivered image artifact. The image history lets you move between available images. Use Zoom to inspect the face, edges, hands, and background before making changes. You can also upload a source image into the studio."
          },
          {
            "type": "section",
            "title": "3. Edit a specific area",
            "copy": "Choose Edit for a general change, or Comment to select an area and describe what should change there. Drag a rectangle around the part of the image you want to revise. Write one clear instruction, such as “Replace the red planet in this area with a small gold star; preserve the astronaut.”"
          },
          {
            "type": "section",
            "title": "4. Send the edit and compare",
            "copy": "Press Add to chat. This prepares your instructions and image context in the conversation; review them and press Send. Wait for the new image, then compare it with the original in the image history. Selecting a tool alone does not run an edit."
          },
          {
            "type": "section",
            "title": "5. Try another tool and download",
            "copy": "Remove targets an unwanted object; Remove BG requests a transparent PNG; Resize reframes to an aspect ratio; Variation makes a new take. Try Resize at 16:9 for a banner and ask to preserve the astronaut. Download the version you prefer from the studio."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Reframe this Star Collector cover as a 16:9 banner. Keep the astronaut and illustrated style, move the subject to the right third, and leave uncluttered dark space on the left. Do not add text."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an edit changes too much, select the original in history and request a smaller change. AI reframing can redraw details; it is not a mechanical crop. For a transparent export, inspect the downloaded PNG in a viewer that shows transparency."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I inspected the image at a useful zoom.",
              "I sent one focused edit from Add to chat and compared the result.",
              "I downloaded the intended version and checked its dimensions."
            ]
          }
        ],
        "title": "Create and edit an image | Gab AI Learn",
        "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, create and edit an image"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-images",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create and edit an image"
      ],
      "navigation": {
        "previous": {
          "label": "Build a playable video game",
          "href": "/learn/work-games"
        },
        "next": {
          "label": "Make an editable document",
          "href": "/learn/work-documents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-documents",
      "title": "Make an editable document",
      "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports.",
      "navIcon": "file-lines",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 706,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The facts and controls match the project.",
          "A direct edit reached the saved state.",
          "I opened the exported DOCX or PDF and checked its layout."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable guide, DOCX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Ask for the file you need",
            "copy": "Select Work and send the prompt below. Specify an editable document, audience, length, sections, and source material. If you have an existing brief, attach it and explain which facts must stay exact. This exercise uses a fictional game, so no research is needed."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable two-page player guide for a fictional browser game called Star Collector. Audience: first-time players age 10 and up. Include a title, short introduction, keyboard and touch controls, the goal of collecting 10 stars, three practical tips, and a troubleshooting table. Use clear headings and short paragraphs. Treat the game details in this brief as the source of truth. Deliver an editable document artifact, not just text in chat."
          },
          {
            "type": "section",
            "title": "2. Open and review the document",
            "copy": "Open the document artifact. Read the headings and controls before adjusting the appearance. Correct any mismatch with your game. A downloadable DOCX or PDF alone is not the same as an editable document artifact: ask Work for the editable version if you need in-app editing."
          },
          {
            "type": "section",
            "title": "3. Make a direct edit",
            "copy": "Click in the document and change the title or a sentence. Select text to use formatting controls, add a list, or insert a table. Use Find and replace for repeated wording. Watch the save status and wait until your changes are saved."
          },
          {
            "type": "section",
            "title": "4. Revise with AI",
            "copy": "Select a paragraph and use AI rewrite for a local wording change, or ask Work in the same task to revise a named section. Be explicit about the document and preserve the facts you have already checked. If the editor reports a newer version while you have local edits, review the choice before replacing your draft."
          },
          {
            "type": "section",
            "title": "5. Export and inspect",
            "copy": "Open Export and choose Word or PDF. Use Page setup to adjust print layout when needed. Open the downloaded file and inspect page breaks, headings, tables, and spacing. Keep the editable document for future revisions; download a fresh export after editing."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In the Star Collector player guide, rewrite only the introduction in a warmer voice and shorten it to 60 words. Preserve the controls, game rules, troubleshooting table, and existing headings. Update the editable document."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an export has an awkward page break, adjust Page setup or shorten that section and export again. If saving fails, keep the editor open and resolve the save error before closing. Use Version history to inspect available saved versions."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "The facts and controls match the project.",
              "A direct edit reached the saved state.",
              "I opened the exported DOCX or PDF and checked its layout."
            ]
          }
        ],
        "title": "Make an editable document | Gab AI Learn",
        "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, make an editable document"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-documents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make an editable document"
      ],
      "navigation": {
        "previous": {
          "label": "Create and edit an image",
          "href": "/learn/work-images"
        },
        "next": {
          "label": "Build a working spreadsheet",
          "href": "/learn/work-spreadsheets"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-spreadsheets",
      "title": "Build a working spreadsheet",
      "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook.",
      "navIcon": "table",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 707,
      "guideSeo": {
        "readMinutes": 14,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The initial grand total is 370.",
          "Changing Testing quantity to 5 updates the total to 395.",
          "The exported XLSX preserves the expected sheets and calculations."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable workbook and XLSX",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 14,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Describe inputs and calculations",
            "copy": "Use Work and send the prompt below. Name each column and the calculations that must use formulas. Request an editable workbook explicitly. The sample inputs are deliberately small so you can verify the answer yourself."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable spreadsheet artifact for a fictional Star Collector launch budget. Sheet Budget: columns Item, Quantity, Unit cost, Total. Rows: Art, 2, 75; Music, 1, 120; Testing, 4, 25. Use formulas for each row total and a grand total, currency formatting, and clearly marked input cells. Add a Summary sheet with the overall budget and a simple chart. Explain which cells I can change. Deliver an editable workbook and an XLSX export."
          },
          {
            "type": "section",
            "title": "2. Open the spreadsheet artifact",
            "copy": "Open the workbook and switch to Budget. Check headers, rows, number formats, and sheet names. Select a calculated cell and inspect its formula in the formula bar. The expected row totals are 150, 120, and 100; the starting grand total is 370."
          },
          {
            "type": "section",
            "title": "3. Test a formula by changing an input",
            "copy": "Change Testing quantity from 4 to 5. Its total should become 125 and the grand total should become 395. Check that Summary agrees. A total that does not change may be hard-coded or reference the wrong cell; ask Work to repair the formula."
          },
          {
            "type": "section",
            "title": "4. Refine the workbook",
            "copy": "Use the editor to adjust cell formatting, add a sheet, or rename a tab. Ask Work to add a contingency calculation in the same workbook. State which inputs and formulas must remain intact, then repeat the known-answer test."
          },
          {
            "type": "section",
            "title": "5. Export and reopen",
            "copy": "Wait for the saved state, then use Export for an Excel workbook. Open the XLSX in your spreadsheet application and check the formulas and chart again. CSV is useful for a flat table, but does not preserve workbook sheets, formatting, or live formulas."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Update this launch budget workbook with a clearly labeled 10% contingency based on the grand total and a total including contingency. Preserve the input rows and existing formulas. With a base total of 395, the contingency should be 39.50 and the final amount 434.50."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a formula is unsupported in the editor, ask Work for a compatible formula or a simpler calculation and verify the export separately. Never judge a spreadsheet only by how polished it looks; change an input and check the result."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "The initial grand total is 370.",
              "Changing Testing quantity to 5 updates the total to 395.",
              "The exported XLSX preserves the expected sheets and calculations."
            ]
          }
        ],
        "title": "Build a working spreadsheet | Gab AI Learn",
        "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, build a working spreadsheet"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-spreadsheets",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a working spreadsheet"
      ],
      "navigation": {
        "previous": {
          "label": "Make an editable document",
          "href": "/learn/work-documents"
        },
        "next": {
          "label": "Create a presentation",
          "href": "/learn/work-presentations"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-presentations",
      "title": "Create a presentation",
      "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export.",
      "navIcon": "chalkboard",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 708,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "Each slide communicates one clear idea.",
          "I edited a slide and reviewed the speaker notes.",
          "I presented the deck and inspected the exported PPTX or PDF."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable deck, PPTX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Brief Work on the story",
            "copy": "Send the starter prompt in Work. Give the audience, purpose, slide count, visual style, and facts. Attach your game cover if you want Work to use it, and identify it as the title-slide image. Ask for an editable presentation artifact."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable five-slide presentation for the fictional Star Collector browser game. Slides: title and one-sentence pitch; how to play; what makes it fun; a simple launch plan; next steps. Audience: friends trying the prototype. Use midnight blue, white, and gold, one clear idea per slide, large readable text, and short speaker notes. Do not invent user numbers or market statistics. Deliver an editable presentation artifact."
          },
          {
            "type": "section",
            "title": "2. Review the slide sequence",
            "copy": "Open the deck artifact and use the slide thumbnails to inspect every slide. Check the narrative before polishing: a viewer should understand the game, its controls, and the next step. Remove unsupported claims or ask Work to replace them with clearly labeled plans."
          },
          {
            "type": "section",
            "title": "3. Edit a slide and its notes",
            "copy": "Select a slide and edit its text in the presentation editor. Shorten a crowded title, adjust the content, and update Speaker notes. Check the save status. Keep explanations in the notes when the slide itself would become too dense."
          },
          {
            "type": "section",
            "title": "4. Rehearse and revise",
            "copy": "Use Present to walk through the deck. Read titles from a distance and check that images are not stretched or text clipped. Ask Work to revise one named slide while preserving the rest. Inspect the revised slide and any surrounding slides it affects."
          },
          {
            "type": "section",
            "title": "5. Export for your audience",
            "copy": "Use Export to download PowerPoint for further editing or PDF for fixed pages. Open the exported file and review all slides. Keep the editable artifact for future updates and re-export after changes."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Revise only slide 3 of the Star Collector presentation. Replace the long paragraph with three short points about simple controls, quick rounds, and replayability. Keep the midnight-blue and gold design and preserve the other four slides."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If text clips in an export, shorten it or request a layout adjustment. A PDF has fixed pages; use the PowerPoint export when your recipient needs to edit the slides."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "Each slide communicates one clear idea.",
              "I edited a slide and reviewed the speaker notes.",
              "I presented the deck and inspected the exported PPTX or PDF."
            ]
          }
        ],
        "title": "Create a presentation | Gab AI Learn",
        "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, create a presentation"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-presentations",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create a presentation"
      ],
      "navigation": {
        "previous": {
          "label": "Build a working spreadsheet",
          "href": "/learn/work-spreadsheets"
        },
        "next": {
          "label": "Make a video, step by step",
          "href": "/learn/work-videos"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "work-videos",
      "title": "Make a video, step by step",
      "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video.",
      "navIcon": "film",
      "navSectionSlug": "module-8-work-projects",
      "navOrder": 709,
      "guideSeo": {
        "readMinutes": 20,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I approved the storyboard and inspected the reference images.",
          "I watched every included clip and selected the intended takes.",
          "I watched the full export with sound and downloaded the correct MP4."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A reviewed video project and MP4",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 20,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Plan before generating",
            "copy": "In Work, send the starter prompt below. Review the proposed shots, duration, aspect ratio, and sound. Start with three short shots so each is easy to inspect. Ask about the expected work and available credits before approving media generation; video generations and revisions use credits and timing can vary."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Help me make a 15-second, 16:9 teaser for a fictional space game called Star Collector. Three shots: a small astronaut notices a gold star; the astronaut dodges an asteroid; a field of stars reveals the adventure ahead. Playful illustrated style, midnight blue and gold, instrumental music, no voiceover or generated lettering. First propose a three-shot storyboard and the assets needed. Wait for my approval before generating media. We will review the reference images, then clips, then export the finished video."
          },
          {
            "type": "section",
            "title": "2. Create and review reference media",
            "copy": "Once the plan is right, tell Work to generate the reference images and pause for review. Open the video project artifact. Reference media groups available assets under Scenes, Characters, Other, and Audio. Select a scene image, inspect the subject and style, and use its available redo or image-choice controls to improve it before animating."
          },
          {
            "type": "section",
            "title": "3. Generate clips from the approved look",
            "copy": "Tell Work which reference images to use and ask it to generate the clips, pausing before the final export. Image choices may be locked while clips are generating. In Clips, select each shot from the timeline and play it. Check motion, continuity, duration, and whether the intended action actually happens."
          },
          {
            "type": "section",
            "title": "4. Improve one shot",
            "copy": "Use the selected shot’s Redo control to describe a specific change. Compare available takes and choose the one you want included. Use the timeline controls to reorder or exclude clips where available. Excluding every clip leaves nothing to export. Keep a shot you like instead of regenerating the whole film."
          },
          {
            "type": "section",
            "title": "5. Export the chosen sequence",
            "copy": "After reviewing all included clips and audio, use the project’s export action. Wait for stitching to finish; editing may be locked during that job. Open Final video and play the full result with sound. Check transitions, beginning and ending, and the music level before choosing Download video."
          },
          {
            "type": "section",
            "title": "6. Make a new version",
            "copy": "For a finished video, use Make changes to describe the next revision. Exports keeps earlier finished versions available. If you change clip choices or order, use Create new export to build an updated film. The previous MP4 does not automatically change when you edit the project."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Redo only shot 2 of the Star Collector teaser. Make the asteroid pass slowly behind the astronaut, with a steady camera and the same character design and blue-and-gold palette. Preserve shots 1 and 3 and the music. Let me compare the revised clip before creating a new export."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a clip fails, retry that shot rather than the whole project. If an export is queued, wait for the active job before requesting another. If no export arrives, use the displayed retry action and report the error in the task. A downloaded clip is only one shot; download from Final video for the complete film."
          },
          {
            "type": "project-checklist",
            "title": "Your finished-project checklist",
            "items": [
              "I approved the storyboard and inspected the reference images.",
              "I watched every included clip and selected the intended takes.",
              "I watched the full export with sound and downloaded the correct MP4."
            ]
          }
        ],
        "title": "Make a video, step by step | Gab AI Learn",
        "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video. Follow the steps in Gab AI Work.",
        "keywords": "Gab AI, Work, artifacts, make a video, step by step"
      },
      "status": "published",
      "badge": "Work workshop",
      "badgeColor": "#123524",
      "canonicalPath": "/learn/work-videos",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make a video, step by step"
      ],
      "navigation": {
        "previous": {
          "label": "Create a presentation",
          "href": "/learn/work-presentations"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work",
      "title": "Work overview",
      "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact.",
      "navIcon": "briefcase",
      "navSectionSlug": "work-agent",
      "navOrder": 20,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I selected Work and sent a small, specific task.",
          "I opened the delivered artifact.",
          "I requested a focused revision in the same task."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "From an idea to a deliverable",
            "copy": "Work is Gab AI’s workspace for tasks that take more than one answer. It can research, write and run code, create files, and use media tools as it works toward your brief. You stay in the same Gab AI conversation to answer questions, review progress, and ask for changes. There is no separate coding agent to install for these lessons."
          },
          {
            "type": "section",
            "title": "How many Work sessions can I run at once?",
            "copy": "Work requires Plus. Plus includes up to 3 active Work sessions at once across your account; Plus Premium includes up to 10. Starting or resuming a session uses a slot. Sessions that are starting or running count, including a running session waiting for your answer. Finished, stopped, and idle sessions do not count. At the limit, wait for one to finish or stop a session and try again. You can still send follow-ups to an already-running session. Plus members who need more sessions can explore Plus Premium. Credits and the separate hourly limit on new Work tasks still apply."
          },
          {
            "type": "section",
            "title": "1. Start a Work task",
            "copy": "Sign in to Gab AI and select Work in the composer. Describe the outcome, attach any references, and name the artifact you want. Review the available Work options and your credits, then press Send. These lessons are free to read; running Work and generating media uses credits."
          },
          {
            "type": "work-prompt",
            "title": "Your first small task",
            "code": "Create an editable one-page project brief for a fictional browser game called Star Collector. Include the goal, audience, controls, three must-have features, and a checklist for testing. Keep the scope small and deliver the document as an artifact."
          },
          {
            "type": "section",
            "title": "2. Follow progress and answer questions",
            "copy": "Read Work’s progress updates to see what it is doing. When it asks for missing details, answer in the task. A plan or progress message is not the finished output. Wait for the delivered file, or read the explanation if Work cannot finish."
          },
          {
            "type": "section",
            "title": "3. Open the artifact",
            "copy": "An artifact is a result you can open: a document, spreadsheet, deck, image, code file, or video project. Open the file card in the conversation to preview or edit it. The editor and available actions depend on the file type and device; these walkthroughs use Gab AI on the web."
          },
          {
            "type": "section",
            "title": "4. Review, then revise",
            "copy": "Compare the result with your brief. Make a direct edit where supported or request a focused change in the same task. Name the artifact and what must stay the same. Download or export the final version and inspect the file you will actually use."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/agentic-workflows"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          },
          {
            "type": "section",
            "title": "Work with Mac folders and continue on mobile",
            "copy": "In the Mac app, choose Project folder before sending a new Work task to connect it to files on your computer. You control read-only or edit access and can review supported changes and recovery originals on the Mac. With Remote, pair the iOS or Android app to start or continue those project chats from your phone. Keep Gab AI open and the Mac awake and online while Work needs local files; files the agent reads are sent to Gab AI."
          },
          {
            "type": "quick-links",
            "title": "Mac app and Remote guides",
            "items": [
              {
                "label": "Get started with the Mac app",
                "href": "/docs/mac-app"
              },
              {
                "label": "Project folders and permissions",
                "href": "/docs/project-folders"
              },
              {
                "label": "Set up Remote on iOS or Android",
                "href": "/docs/remote"
              }
            ]
          }
        ],
        "title": "Work overview | Gab AI Docs",
        "description": "Give Gab AI a project and follow it from a clear brief to a finished artifact. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, work overview"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work",
      "updatedAt": "2026-09-15T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "work overview"
      ],
      "navigation": {
        "next": {
          "label": "Writing a good Work task",
          "href": "/docs/work-prompts"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-prompts",
      "title": "Writing a good Work task",
      "description": "Specify the outcome, references, constraints, review points, and definition of done.",
      "navIcon": "list-check",
      "navSectionSlug": "work-agent",
      "navOrder": 21,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "My brief names an output format and audience.",
          "My sources and scope are explicit.",
          "I included a review point and a concrete acceptance check."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Make success observable",
            "copy": "A good Work brief describes the result someone will use. Include the audience, exact output formats, source material, limits, and a test that proves the work is ready. State assumptions Work may make and which choices need your input."
          },
          {
            "type": "work-prompt",
            "title": "A reusable Work brief",
            "code": "Outcome: Create [deliverable] for [audience].\nContext: Use [attached files or facts]. Preserve [details].\nFormat: Deliver [editable artifact] plus [export format].\nScope: Include [must-haves]. Keep this first version to [limit].\nReview: Pause after [storyboard, outline, or prototype] so I can review it.\nDone means: Check [specific tests] and explain anything incomplete."
          },
          {
            "type": "section",
            "title": "1. Attach and explain references",
            "copy": "Upload the relevant source and identify its role: “Use this as factual source material” or “Match this visual style.” State which file is authoritative if several disagree. Work only has the context available in the task; do not assume it can see a file on your device that you have not attached."
          },
          {
            "type": "section",
            "title": "2. Choose an appropriate review point",
            "copy": "For a short document, ask for a complete first draft. For a video, approve the storyboard and references before generating every clip. A requested pause is an instruction to Work, so check that it has reached the review point before approving the next phase."
          },
          {
            "type": "section",
            "title": "3. Define checks and delivery",
            "copy": "For a game, list controls, collisions and restart. For a budget, give a known total. For a deck, ask it to inspect every slide. Request the actual files and an explanation of what was verified, rather than treating a confident completion message as proof."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/briefing-agents"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Writing a good Work task | Gab AI Docs",
        "description": "Specify the outcome, references, constraints, review points, and definition of done. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, writing a good work task"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-prompts",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "writing a good work task"
      ],
      "navigation": {
        "previous": {
          "label": "Work overview",
          "href": "/docs/work"
        },
        "next": {
          "label": "Make a video, step by step",
          "href": "/docs/work-videos"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-artifacts",
      "title": "Artifact editors and exports",
      "description": "Know which artifact to request, what you can edit, and how to take your work with you.",
      "navIcon": "layer-group",
      "navSectionSlug": "work-agent",
      "navOrder": 24,
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Work",
        "teaches": [
          "I can name the artifact and export format for my project.",
          "I know when Add to chat still requires Send.",
          "I understand that a revised project needs a fresh export."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Choose the result before choosing the tool",
            "copy": "Tell Work what you want to make. It selects tools while you describe the result and review it. Ask for the editable artifact as well as any final download you need. An answer pasted into chat, an editable project, and an exported file serve different purposes."
          },
          {
            "type": "unordered-list",
            "title": "What each artifact is for",
            "items": [
              "Document: edit text, headings and tables, use AI rewrite, then export Word or PDF.",
              "Spreadsheet: edit cells, formulas, formatting and sheets, then export Excel.",
              "Presentation: edit slides and speaker notes, rehearse with Present, then export PowerPoint or PDF.",
              "Image: inspect versions, select an area, request edits or reframing, then download the chosen image.",
              "Code and HTML: run supported HTML in Preview, inspect Code (or Source for Markdown) and available Changes, and point to revisions with Select or Highlight. Source is a viewer; ask Work for code changes.",
              "Video project: review reference media and clips, compare takes, and create a final video export."
            ]
          },
          {
            "type": "section",
            "title": "1. Open the delivered file",
            "copy": "Choose the artifact card in the Work conversation. A supported HTML file opens a preview, while other code types show source. A PDF is a finished document preview; ask for an editable document when you need to change its content inside Gab AI."
          },
          {
            "type": "section",
            "title": "2. Use the right kind of edit",
            "copy": "Text, cells and slides support direct editing in their respective editors. In the image studio and HTML preview, Add to chat prepares an AI revision: you still review and send it in chat. Saving a direct edit and asking Work to generate a new result are different actions."
          },
          {
            "type": "section",
            "title": "3. Keep the deliverable current",
            "copy": "Wait for saves to finish, compare the revised artifact with the original, and export again after changes. For a multi-file project, ask Work for all required assets and instructions. Check the downloaded file before sending it to someone else."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/agent-tools"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Artifact editors and exports | Gab AI Docs",
        "description": "Know which artifact to request, what you can edit, and how to take your work with you. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, artifact editors and exports"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-artifacts",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "artifact editors and exports"
      ],
      "navigation": {
        "previous": {
          "label": "Files, code & games",
          "href": "/docs/work-files"
        },
        "next": {
          "label": "Build a playable video game",
          "href": "/docs/work-games"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-games",
      "title": "Build a playable video game",
      "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game.",
      "navIcon": "gamepad",
      "navSectionSlug": "work-agent",
      "navOrder": 25,
      "guideSeo": {
        "readMinutes": 18,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I can move, collect stars, win or lose, and restart without reloading.",
          "The controls fit on a small screen and do not cover the game.",
          "I reviewed a revision and tested the downloaded HTML."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A playable HTML game",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 18,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Start with one complete game loop",
            "copy": "Open Gab AI, select Work, and paste the starter prompt below. Keep the first version small: movement, a goal, obstacles, and restart. Work will create the files and check the game. Answer any question about the controls or scope in the same task."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Build Star Collector, a small playable browser game in one self-contained HTML file with embedded CSS and JavaScript. Move with arrow keys or WASD and on-screen touch controls. Collect 10 stars, avoid three moving obstacles, show score and a win or lose screen, and include a working Restart button. Use a midnight-blue space theme. No external assets, logins, or network calls. Test movement, collisions, scoring, restart, and a narrow mobile layout. Deliver the HTML game as a file artifact and tell me what you tested."
          },
          {
            "type": "section",
            "title": "2. Open the code artifact and play",
            "copy": "When Work delivers the HTML file, open its file card. Preview runs the game; click inside it before using keyboard controls. Collect stars, hit an obstacle, reach the win screen, and restart. Use the preview device selector to check a phone-sized layout. Test touch controls on a real touch device too."
          },
          {
            "type": "section",
            "title": "3. Point to a change",
            "copy": "In the HTML preview, use Select to pick a page element or Highlight to mark a region. Choose Ask for changes and describe the revision. A game drawn on a canvas is usually one element: highlight the region and describe the gameplay issue in words. Add to chat prepares the request; review it and press Send in the conversation."
          },
          {
            "type": "section",
            "title": "4. Inspect the revision",
            "copy": "Open the updated artifact. The Code tab lets you read the HTML file; Changes appears when an earlier source is available. For a simple static text element, Select may offer Edit text and Save copy. Use Work for game logic and dynamic text. Replay your test sequence after every change."
          },
          {
            "type": "section",
            "title": "5. Download and test the file",
            "copy": "Download the final HTML and open it in your browser. Because this exercise embeds its assets, it should run without a server. Check the downloaded copy as well as the preview. If you later request separate assets, ask Work to package the complete folder with instructions."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In Star Collector, make the player 20% slower and enlarge the touch controls. Keep the scoring, star count, visual style, and restart behavior. Update the existing game artifact and retest keyboard movement, collisions, winning, and restart."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If the preview is blank, try Restart preview, then tell Work what you see and ask it to fix the HTML artifact. If controls do nothing, click into Preview and leave Select or Highlight mode. If only the download fails, report that separately from the preview."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-games"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Build a playable video game | Gab AI Docs",
        "description": "Build Star Collector, play it in the code artifact, point to changes, and download your game. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, build a playable video game"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-games",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a playable video game"
      ],
      "navigation": {
        "previous": {
          "label": "Artifact editors and exports",
          "href": "/docs/work-artifacts"
        },
        "next": {
          "label": "Create and edit an image",
          "href": "/docs/work-images"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-images",
      "title": "Create and edit an image",
      "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format.",
      "navIcon": "image",
      "navSectionSlug": "work-agent",
      "navOrder": 26,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I inspected the image at a useful zoom.",
          "I sent one focused edit from Add to chat and compared the result.",
          "I downloaded the intended version and checked its dimensions."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An image and a revised version",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Give Work an image brief",
            "copy": "Select Work and send the prompt below. Name the subject, style, shape, and where the image will be used. You can attach a reference image and explain which details to preserve. For this exercise, keep the cover free of lettering so you can judge the composition first."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create a square cover image for a browser game called Star Collector: a friendly small astronaut reaching for glowing gold stars, midnight-blue space, simple bold shapes, playful illustrated style. Leave clear space at the top for a title I will add later. Do not generate lettering. Deliver the image as an artifact."
          },
          {
            "type": "section",
            "title": "2. Open the image studio",
            "copy": "Open the delivered image artifact. The image history lets you move between available images. Use Zoom to inspect the face, edges, hands, and background before making changes. You can also upload a source image into the studio."
          },
          {
            "type": "section",
            "title": "3. Edit a specific area",
            "copy": "Choose Edit for a general change, or Comment to select an area and describe what should change there. Drag a rectangle around the part of the image you want to revise. Write one clear instruction, such as “Replace the red planet in this area with a small gold star; preserve the astronaut.”"
          },
          {
            "type": "section",
            "title": "4. Send the edit and compare",
            "copy": "Press Add to chat. This prepares your instructions and image context in the conversation; review them and press Send. Wait for the new image, then compare it with the original in the image history. Selecting a tool alone does not run an edit."
          },
          {
            "type": "section",
            "title": "5. Try another tool and download",
            "copy": "Remove targets an unwanted object; Remove BG requests a transparent PNG; Resize reframes to an aspect ratio; Variation makes a new take. Try Resize at 16:9 for a banner and ask to preserve the astronaut. Download the version you prefer from the studio."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Reframe this Star Collector cover as a 16:9 banner. Keep the astronaut and illustrated style, move the subject to the right third, and leave uncluttered dark space on the left. Do not add text."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an edit changes too much, select the original in history and request a smaller change. AI reframing can redraw details; it is not a mechanical crop. For a transparent export, inspect the downloaded PNG in a viewer that shows transparency."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-images"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Create and edit an image | Gab AI Docs",
        "description": "Make a game cover, select an area in the image artifact, remove distractions, and create a new format. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, create and edit an image"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-images",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create and edit an image"
      ],
      "navigation": {
        "previous": {
          "label": "Build a playable video game",
          "href": "/docs/work-games"
        },
        "next": {
          "label": "Make an editable document",
          "href": "/docs/work-documents"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-documents",
      "title": "Make an editable document",
      "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports.",
      "navIcon": "file-lines",
      "navSectionSlug": "work-agent",
      "navOrder": 27,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The facts and controls match the project.",
          "A direct edit reached the saved state.",
          "I opened the exported DOCX or PDF and checked its layout."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable guide, DOCX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Ask for the file you need",
            "copy": "Select Work and send the prompt below. Specify an editable document, audience, length, sections, and source material. If you have an existing brief, attach it and explain which facts must stay exact. This exercise uses a fictional game, so no research is needed."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable two-page player guide for a fictional browser game called Star Collector. Audience: first-time players age 10 and up. Include a title, short introduction, keyboard and touch controls, the goal of collecting 10 stars, three practical tips, and a troubleshooting table. Use clear headings and short paragraphs. Treat the game details in this brief as the source of truth. Deliver an editable document artifact, not just text in chat."
          },
          {
            "type": "section",
            "title": "2. Open and review the document",
            "copy": "Open the document artifact. Read the headings and controls before adjusting the appearance. Correct any mismatch with your game. A downloadable DOCX or PDF alone is not the same as an editable document artifact: ask Work for the editable version if you need in-app editing."
          },
          {
            "type": "section",
            "title": "3. Make a direct edit",
            "copy": "Click in the document and change the title or a sentence. Select text to use formatting controls, add a list, or insert a table. Use Find and replace for repeated wording. Watch the save status and wait until your changes are saved."
          },
          {
            "type": "section",
            "title": "4. Revise with AI",
            "copy": "Select a paragraph and use AI rewrite for a local wording change, or ask Work in the same task to revise a named section. Be explicit about the document and preserve the facts you have already checked. If the editor reports a newer version while you have local edits, review the choice before replacing your draft."
          },
          {
            "type": "section",
            "title": "5. Export and inspect",
            "copy": "Open Export and choose Word or PDF. Use Page setup to adjust print layout when needed. Open the downloaded file and inspect page breaks, headings, tables, and spacing. Keep the editable document for future revisions; download a fresh export after editing."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "In the Star Collector player guide, rewrite only the introduction in a warmer voice and shorten it to 60 words. Preserve the controls, game rules, troubleshooting table, and existing headings. Update the editable document."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If an export has an awkward page break, adjust Page setup or shorten that section and export again. If saving fails, keep the editor open and resolve the save error before closing. Use Version history to inspect available saved versions."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-documents"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Make an editable document | Gab AI Docs",
        "description": "Turn a brief into a formatted document, edit it directly, and check Word and PDF exports. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, make an editable document"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-documents",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make an editable document"
      ],
      "navigation": {
        "previous": {
          "label": "Create and edit an image",
          "href": "/docs/work-images"
        },
        "next": {
          "label": "Build a working spreadsheet",
          "href": "/docs/work-spreadsheets"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-spreadsheets",
      "title": "Build a working spreadsheet",
      "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook.",
      "navIcon": "table",
      "navSectionSlug": "work-agent",
      "navOrder": 28,
      "guideSeo": {
        "readMinutes": 14,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "The initial grand total is 370.",
          "Changing Testing quantity to 5 updates the total to 395.",
          "The exported XLSX preserves the expected sheets and calculations."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable workbook and XLSX",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 14,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Describe inputs and calculations",
            "copy": "Use Work and send the prompt below. Name each column and the calculations that must use formulas. Request an editable workbook explicitly. The sample inputs are deliberately small so you can verify the answer yourself."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable spreadsheet artifact for a fictional Star Collector launch budget. Sheet Budget: columns Item, Quantity, Unit cost, Total. Rows: Art, 2, 75; Music, 1, 120; Testing, 4, 25. Use formulas for each row total and a grand total, currency formatting, and clearly marked input cells. Add a Summary sheet with the overall budget and a simple chart. Explain which cells I can change. Deliver an editable workbook and an XLSX export."
          },
          {
            "type": "section",
            "title": "2. Open the spreadsheet artifact",
            "copy": "Open the workbook and switch to Budget. Check headers, rows, number formats, and sheet names. Select a calculated cell and inspect its formula in the formula bar. The expected row totals are 150, 120, and 100; the starting grand total is 370."
          },
          {
            "type": "section",
            "title": "3. Test a formula by changing an input",
            "copy": "Change Testing quantity from 4 to 5. Its total should become 125 and the grand total should become 395. Check that Summary agrees. A total that does not change may be hard-coded or reference the wrong cell; ask Work to repair the formula."
          },
          {
            "type": "section",
            "title": "4. Refine the workbook",
            "copy": "Use the editor to adjust cell formatting, add a sheet, or rename a tab. Ask Work to add a contingency calculation in the same workbook. State which inputs and formulas must remain intact, then repeat the known-answer test."
          },
          {
            "type": "section",
            "title": "5. Export and reopen",
            "copy": "Wait for the saved state, then use Export for an Excel workbook. Open the XLSX in your spreadsheet application and check the formulas and chart again. CSV is useful for a flat table, but does not preserve workbook sheets, formatting, or live formulas."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Update this launch budget workbook with a clearly labeled 10% contingency based on the grand total and a total including contingency. Preserve the input rows and existing formulas. With a base total of 395, the contingency should be 39.50 and the final amount 434.50."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a formula is unsupported in the editor, ask Work for a compatible formula or a simpler calculation and verify the export separately. Never judge a spreadsheet only by how polished it looks; change an input and check the result."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-spreadsheets"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Build a working spreadsheet | Gab AI Docs",
        "description": "Create a launch budget, test formulas with a known answer, edit cells, and export your workbook. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, build a working spreadsheet"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-spreadsheets",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "build a working spreadsheet"
      ],
      "navigation": {
        "previous": {
          "label": "Make an editable document",
          "href": "/docs/work-documents"
        },
        "next": {
          "label": "Create a presentation",
          "href": "/docs/work-presentations"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-presentations",
      "title": "Create a presentation",
      "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export.",
      "navIcon": "chalkboard",
      "navSectionSlug": "work-agent",
      "navOrder": 29,
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "Each slide communicates one clear idea.",
          "I edited a slide and reviewed the speaker notes.",
          "I presented the deck and inspected the exported PPTX or PDF."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "An editable deck, PPTX, and PDF",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 12,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Brief Work on the story",
            "copy": "Send the starter prompt in Work. Give the audience, purpose, slide count, visual style, and facts. Attach your game cover if you want Work to use it, and identify it as the title-slide image. Ask for an editable presentation artifact."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Create an editable five-slide presentation for the fictional Star Collector browser game. Slides: title and one-sentence pitch; how to play; what makes it fun; a simple launch plan; next steps. Audience: friends trying the prototype. Use midnight blue, white, and gold, one clear idea per slide, large readable text, and short speaker notes. Do not invent user numbers or market statistics. Deliver an editable presentation artifact."
          },
          {
            "type": "section",
            "title": "2. Review the slide sequence",
            "copy": "Open the deck artifact and use the slide thumbnails to inspect every slide. Check the narrative before polishing: a viewer should understand the game, its controls, and the next step. Remove unsupported claims or ask Work to replace them with clearly labeled plans."
          },
          {
            "type": "section",
            "title": "3. Edit a slide and its notes",
            "copy": "Select a slide and edit its text in the presentation editor. Shorten a crowded title, adjust the content, and update Speaker notes. Check the save status. Keep explanations in the notes when the slide itself would become too dense."
          },
          {
            "type": "section",
            "title": "4. Rehearse and revise",
            "copy": "Use Present to walk through the deck. Read titles from a distance and check that images are not stretched or text clipped. Ask Work to revise one named slide while preserving the rest. Inspect the revised slide and any surrounding slides it affects."
          },
          {
            "type": "section",
            "title": "5. Export for your audience",
            "copy": "Use Export to download PowerPoint for further editing or PDF for fixed pages. Open the exported file and review all slides. Keep the editable artifact for future updates and re-export after changes."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Revise only slide 3 of the Star Collector presentation. Replace the long paragraph with three short points about simple controls, quick rounds, and replayability. Keep the midnight-blue and gold design and preserve the other four slides."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If text clips in an export, shorten it or request a layout adjustment. A PDF has fixed pages; use the PowerPoint export when your recipient needs to edit the slides."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-presentations"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Create a presentation | Gab AI Docs",
        "description": "Build a five-slide pitch, edit the slides and speaker notes, rehearse, and export. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, create a presentation"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-presentations",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "create a presentation"
      ],
      "navigation": {
        "previous": {
          "label": "Build a working spreadsheet",
          "href": "/docs/work-spreadsheets"
        },
        "next": {
          "label": "Mac app",
          "href": "/docs/mac-app"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-videos",
      "title": "Make a video, step by step",
      "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video.",
      "navIcon": "film",
      "navSectionSlug": "work-agent",
      "navOrder": 22,
      "guideSeo": {
        "readMinutes": 20,
        "difficulty": "Beginner",
        "articleSection": "Work Projects",
        "teaches": [
          "I approved the storyboard and inspected the reference images.",
          "I watched every included clip and selected the intended takes.",
          "I watched the full export with sound and downloaded the correct MP4."
        ]
      },
      "meta": {
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "A reviewed video project and MP4",
              "A focused revision you can compare with your original",
              "A checked download you can use outside Gab AI"
            ],
            "readTime": 20,
            "prerequisites": [
              {
                "label": "Start with Meet Gab AI Work",
                "href": "/learn/agentic-workflows"
              }
            ]
          },
          {
            "type": "section",
            "title": "1. Plan before generating",
            "copy": "In Work, send the starter prompt below. Review the proposed shots, duration, aspect ratio, and sound. Start with three short shots so each is easy to inspect. Ask about the expected work and available credits before approving media generation; video generations and revisions use credits and timing can vary."
          },
          {
            "type": "work-prompt",
            "title": "Copy this starter prompt",
            "code": "Help me make a 15-second, 16:9 teaser for a fictional space game called Star Collector. Three shots: a small astronaut notices a gold star; the astronaut dodges an asteroid; a field of stars reveals the adventure ahead. Playful illustrated style, midnight blue and gold, instrumental music, no voiceover or generated lettering. First propose a three-shot storyboard and the assets needed. Wait for my approval before generating media. We will review the reference images, then clips, then export the finished video."
          },
          {
            "type": "section",
            "title": "2. Create and review reference media",
            "copy": "Once the plan is right, tell Work to generate the reference images and pause for review. Open the video project artifact. Reference media groups available assets under Scenes, Characters, Other, and Audio. Select a scene image, inspect the subject and style, and use its available redo or image-choice controls to improve it before animating."
          },
          {
            "type": "section",
            "title": "3. Generate clips from the approved look",
            "copy": "Tell Work which reference images to use and ask it to generate the clips, pausing before the final export. Image choices may be locked while clips are generating. In Clips, select each shot from the timeline and play it. Check motion, continuity, duration, and whether the intended action actually happens."
          },
          {
            "type": "section",
            "title": "4. Improve one shot",
            "copy": "Use the selected shot’s Redo control to describe a specific change. Compare available takes and choose the one you want included. Use the timeline controls to reorder or exclude clips where available. Excluding every clip leaves nothing to export. Keep a shot you like instead of regenerating the whole film."
          },
          {
            "type": "section",
            "title": "5. Export the chosen sequence",
            "copy": "After reviewing all included clips and audio, use the project’s export action. Wait for stitching to finish; editing may be locked during that job. Open Final video and play the full result with sound. Check transitions, beginning and ending, and the music level before choosing Download video."
          },
          {
            "type": "section",
            "title": "6. Make a new version",
            "copy": "For a finished video, use Make changes to describe the next revision. Exports keeps earlier finished versions available. If you change clip choices or order, use Create new export to build an updated film. The previous MP4 does not automatically change when you edit the project."
          },
          {
            "type": "work-prompt",
            "title": "Try this focused revision",
            "code": "Redo only shot 2 of the Star Collector teaser. Make the asteroid pass slowly behind the astronaut, with a steady camera and the same character design and blue-and-gold palette. Preserve shots 1 and 3 and the music. Let me compare the revised clip before creating a new export."
          },
          {
            "type": "section",
            "title": "If something goes wrong",
            "copy": "If a clip fails, retry that shot rather than the whole project. If an export is queued, wait for the active job before requesting another. If no export arrives, use the displayed retry action and report the error in the task. A downloaded clip is only one shot; download from Final video for the complete film."
          },
          {
            "type": "quick-links",
            "title": "Practice this workflow",
            "items": [
              {
                "label": "Open the hands-on lesson",
                "href": "/learn/work-videos"
              },
              {
                "label": "Explore all Work projects",
                "href": "/learn"
              }
            ]
          }
        ],
        "title": "Make a video, step by step | Gab AI Docs",
        "description": "Plan a three-shot trailer, review reference media and clips, revise a shot, and export your video. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, make a video, step by step"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-videos",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "make a video, step by step"
      ],
      "navigation": {
        "previous": {
          "label": "Writing a good Work task",
          "href": "/docs/work-prompts"
        },
        "next": {
          "label": "Files, code & games",
          "href": "/docs/work-files"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "work-files",
      "title": "Files, code & games",
      "description": "Choose a file format and follow a complete Work tutorial, from first brief to final export.",
      "navIcon": "file",
      "navSectionSlug": "work-agent",
      "navOrder": 23,
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Work"
      },
      "meta": {
        "pageCopy": [
          {
            "type": "section",
            "title": "Ask Work for the artifact and the export",
            "copy": "Start with the editable result you want, then name the file format you need to deliver. The tutorials below cover creating the artifact, making a change, and checking the downloaded file."
          },
          {
            "type": "quick-links",
            "title": "Choose a walkthrough",
            "items": [
              {
                "label": "Build a playable video game",
                "href": "/docs/work-games"
              },
              {
                "label": "Create and edit an image",
                "href": "/docs/work-images"
              },
              {
                "label": "Make an editable document",
                "href": "/docs/work-documents"
              },
              {
                "label": "Build a working spreadsheet",
                "href": "/docs/work-spreadsheets"
              },
              {
                "label": "Create a presentation",
                "href": "/docs/work-presentations"
              },
              {
                "label": "Make a video, step by step",
                "href": "/docs/work-videos"
              }
            ]
          },
          {
            "type": "section",
            "title": "Check the final file",
            "copy": "Opening a preview is only part of review. Download the deliverable and open it in its intended app. For multiple files, ask Work for a complete package with assets and instructions. For another round of edits, return to the same Work task and identify the artifact."
          }
        ],
        "title": "Files, code & games | Gab AI Docs",
        "description": "Choose a file format and follow a complete Work tutorial, from first brief to final export. Read the Gab AI Work documentation.",
        "keywords": "Gab AI, Work, artifacts, files, code & games"
      },
      "status": "published",
      "badge": "Work",
      "badgeColor": "#123524",
      "canonicalPath": "/docs/work-files",
      "updatedAt": "2026-09-06T12:00:00.000Z",
      "searchKeywords": [
        "work",
        "artifact",
        "files, code & games"
      ],
      "navigation": {
        "previous": {
          "label": "Make a video, step by step",
          "href": "/docs/work-videos"
        },
        "next": {
          "label": "Artifact editors and exports",
          "href": "/docs/work-artifacts"
        }
      },
      "publishedAt": "2026-09-06T12:00:00.000Z"
    }
  ]
}
